import { SuggestionData } from 'ckeditor5-premium-features'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'

/** This module provides functions to detect changes in the content of a plain text editor */
export interface ChangeDetectionParams {
    /** The unsaved data in the editor */
    unsavedData: string

    /** The original text before any changes were made */
    originalText: string

    /** Optional node to add, which may contain an id and type */
    nodeToAdd?: { id?: string; type?: string } | null

    /** Previous content item view nodes, used to determine if we should revert */
    previousContentItemViewNodes?: Partial<ContentItemViewNodesType>[] | null

    /** Initial suggestions available in the editor */
    initialSuggestions: SuggestionData[]

    /** Flag indicating if the user has deleted the whole text */
    hasDeletedWholeText: boolean
}

/** Interface for the result of change detection in the editor */
export interface ChangeDetectionResult {
    /** Indicates if there are changes in the content */
    hasChanges: boolean

    /** Indicates if we should revert to the previous content state */
    shouldRevertToPrevious: boolean

    /** Indicates if the editor should be deactivated */
    shouldDeactivate: boolean

    /** Indicates if new content should be added */
    shouldAddNewContent: boolean

    /** Indicates if an optimistic update should be executed */
    shouldExecuteOptimistic: boolean
}

/**
 * Checks if content update should be skipped.
 * Empty content always skips (deletions handled via hasDeletedWholeText).
 */
const shouldSkipContentUpdate = (
    unsavedDataString: string,
    originalText: string
) => {
    // no content = skip save (deletion is handled via hasDeletedWholeText)
    if (!unsavedDataString) {
        return true
    }

    return unsavedDataString === originalText
}

const shouldRevertToPrevious = (
    nodeToAdd?: { id?: string } | null,
    previousContentItemViewNodes?: Partial<ContentItemViewNodesType>[] | null
) => {
    return Boolean(nodeToAdd?.id && previousContentItemViewNodes)
}

const shouldDeactivateEditor = (
    hasSuggestions: boolean,
    hasDeletedWholeText: boolean
) => {
    return !hasSuggestions || !hasDeletedWholeText
}

/**
 * Determines what action should be taken based on the current state of the editor
 * @param params - The parameters for change detection
 */
export const detectContentChanges = (
    params: ChangeDetectionParams
): ChangeDetectionResult => {
    const {
        unsavedData,
        originalText,
        nodeToAdd,
        previousContentItemViewNodes,
        initialSuggestions,
        hasDeletedWholeText,
    } = params

    const skipContentUpdate = shouldSkipContentUpdate(unsavedData, originalText)
    const hasSuggestions = initialSuggestions.length > 0

    // Case 1a: Skip content update AND revert to previous state
    if (
        skipContentUpdate &&
        shouldRevertToPrevious(nodeToAdd, previousContentItemViewNodes)
    ) {
        return {
            hasChanges: false,
            shouldRevertToPrevious: true,
            shouldDeactivate: false,
            shouldAddNewContent: false,
            shouldExecuteOptimistic: false,
        }
    }

    // Case 1b: Skip content update - check if we should deactivate
    if (skipContentUpdate) {
        const shouldDeactivate = shouldDeactivateEditor(
            hasSuggestions,
            hasDeletedWholeText
        )

        return {
            hasChanges: false,
            shouldRevertToPrevious: false,
            shouldDeactivate,
            shouldAddNewContent: false,
            shouldExecuteOptimistic: !shouldDeactivate,
        }
    }

    // Case 2: Has pending changes AND adding new content
    if (nodeToAdd?.id) {
        return {
            hasChanges: true,
            shouldRevertToPrevious: false,
            shouldDeactivate: false,
            shouldAddNewContent: true,
            shouldExecuteOptimistic: false,
        }
    }

    // Case 3: Has pending changes - execute optimistic update
    return {
        hasChanges: true,
        shouldRevertToPrevious: false,
        shouldDeactivate: false,
        shouldAddNewContent: false,
        shouldExecuteOptimistic: true,
    }
}

/**
 * Helper to check if content has meaningful changes
 */
export const hasContentChanged = (
    unsavedData: string,
    originalText: string
) => {
    return Boolean(unsavedData) && unsavedData !== originalText
}
